import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { User, UserType } from '@/types'
import { apiClient } from '@/lib/api'

interface UserContextType {
  currentUser: User | null
  userType: UserType
  /** The active user type, accounting for impersonation (use this for role-based UI gating). */
  effectiveUserType: UserType
  setCurrentUser: (user: User | null) => void
  isLoading: boolean
  // Role helpers
  isAdmin: boolean
  isSuperAdmin: boolean
  isSupport: boolean
  isMarketing: boolean
  isOrders: boolean
  isQuotes: boolean
  isRfp: boolean
  /** Full Support-Ticket management (Admin/Super Admin/Support/Orders/Quotes). Others are view-only. */
  canManageTickets: boolean
  // Impersonation
  impersonatedUser: User | null
  isImpersonating: boolean
  startImpersonation: (userId: string) => Promise<void>
  endImpersonation: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

const STORAGE_KEY = 'gracklecrm_user_type'
const USER_ID_STORAGE_KEY = 'gracklecrm_current_user_id'
const IMPERSONATION_KEY = 'gracklecrm_impersonated_user_id'

interface UserProviderProps {
  children: ReactNode
}

export function UserProvider({ children }: UserProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [userType, setUserTypeState] = useState<UserType>('Admin')
  const [isLoading, setIsLoading] = useState(true)
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null)

  // Load user type from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (
      stored === 'Super Admin' ||
      stored === 'Admin' ||
      stored === 'User' ||
      stored === 'Marketing' ||
      stored === 'Orders' ||
      stored === 'Quotes' ||
      stored === 'Support' ||
      stored === 'RFP'
    ) {
      setUserTypeState(stored)
    }

    // Load user data only if we have a valid token
    const loadUser = async () => {
      try {
        // Check if user is already stored from OAuth
        const storedUserJson = localStorage.getItem('user')
        if (storedUserJson) {
          const userData = JSON.parse(storedUserJson)
          setCurrentUser(userData)
          if (userData.user_type) {
            setUserTypeState(userData.user_type)
            localStorage.setItem(STORAGE_KEY, userData.user_type)
          }
          setIsLoading(false)
          return
        }

        const token = localStorage.getItem('accessToken')
        const savedUserId = localStorage.getItem(USER_ID_STORAGE_KEY)

        // Only attempt to load user if we have both token and user ID
        if (!token || !savedUserId) {
          setIsLoading(false)
          return
        }

        const userData = await apiClient.getUser(savedUserId)

        // Preserve profile picture from localStorage if it exists
        // (picture comes from OAuth but isn't stored in database)
        const cachedUserJson = localStorage.getItem('user')
        if (cachedUserJson) {
          try {
            const cachedUser = JSON.parse(cachedUserJson)
            if (cachedUser.picture) {
              ;(userData as any).picture = cachedUser.picture
            }
          } catch {
            // Ignore parse errors
          }
        }

        setCurrentUser(userData)
        // If user has a user_type in the database, use that
        if (userData.user_type) {
          setUserTypeState(userData.user_type)
          localStorage.setItem(STORAGE_KEY, userData.user_type)
        }
      } catch (error) {
        console.error('Error loading user:', error)
        // Clear invalid auth data
        localStorage.removeItem('accessToken')
        localStorage.removeItem(USER_ID_STORAGE_KEY)
      } finally {
        setIsLoading(false)
      }
    }

    loadUser()
  }, [])

  // Set access token when current user changes (demo mode only)
  // NOTE: This is disabled to allow OAuth JWT tokens to work
  // If you need demo mode, uncomment this useEffect
  // useEffect(() => {
  //   if (currentUser) {
  //     // Store the demo token format expected by the backend
  //     const token = `demo-token-${currentUser.id}`;
  //     localStorage.setItem('accessToken', token);
  //   } else {
  //     localStorage.removeItem('accessToken');
  //   }
  // }, [currentUser]);

  // Load impersonation state from localStorage on mount
  useEffect(() => {
    const storedImpersonationId = localStorage.getItem(IMPERSONATION_KEY)
    const hasAdminPrivileges =
      currentUser?.user_type === 'Super Admin' || currentUser?.user_type === 'Admin'
    const userIsSuperAdmin = currentUser?.user_type === 'Super Admin'

    if (storedImpersonationId && hasAdminPrivileges) {
      // Restore impersonation state
      apiClient
        .getUser(storedImpersonationId)
        .then((user) => {
          // Super Admin can impersonate Admin and User, Admin can only impersonate User
          // Neither can impersonate Super Admin
          const canImpersonate =
            user &&
            user.user_type !== 'Super Admin' &&
            (userIsSuperAdmin || user.user_type !== 'Admin')

          if (canImpersonate) {
            setImpersonatedUser(user)
          } else {
            // Invalid impersonation target, clear it
            localStorage.removeItem(IMPERSONATION_KEY)
          }
        })
        .catch(() => {
          localStorage.removeItem(IMPERSONATION_KEY)
        })
    }
  }, [currentUser])

  // Start impersonating a user
  const startImpersonation = useCallback(
    async (userId: string) => {
      const hasAdminPrivileges =
        currentUser?.user_type === 'Super Admin' || currentUser?.user_type === 'Admin'
      if (!currentUser || !hasAdminPrivileges) {
        throw new Error('Only administrators can impersonate users')
      }

      try {
        const response = await apiClient.startImpersonation(userId)
        const targetUser = response.targetUser

        setImpersonatedUser(targetUser)
        localStorage.setItem(IMPERSONATION_KEY, targetUser.id)
      } catch (error) {
        console.error('Failed to start impersonation:', error)
        throw error
      }
    },
    [currentUser]
  )

  // End impersonation
  const endImpersonation = useCallback(() => {
    setImpersonatedUser(null)
    localStorage.removeItem(IMPERSONATION_KEY)
  }, [])

  // Wrapper for setCurrentUser that persists to localStorage
  const handleSetCurrentUser = (user: User | null) => {
    setCurrentUser(user)
    if (user) {
      localStorage.setItem(USER_ID_STORAGE_KEY, user.id)
      // Token will be set by the useEffect above
    } else {
      localStorage.removeItem(USER_ID_STORAGE_KEY)
      localStorage.removeItem('accessToken')
      // Clear impersonation on logout
      setImpersonatedUser(null)
      localStorage.removeItem(IMPERSONATION_KEY)
    }
  }

  const isImpersonating = !!impersonatedUser
  const effectiveUserType = impersonatedUser?.user_type || userType
  const isSuperAdmin = effectiveUserType === 'Super Admin'
  const isAdmin = isSuperAdmin || effectiveUserType === 'Admin'
  const isSupport = effectiveUserType === 'Support'
  const isMarketing = effectiveUserType === 'Marketing'
  const isOrders = effectiveUserType === 'Orders'
  const isQuotes = effectiveUserType === 'Quotes'
  const isRfp = effectiveUserType === 'RFP'
  // Full Support-Ticket management (create directly, edit, comment, change
  // status, delete) is limited to the support team + admins. Other roles
  // (Sales/User, Marketing, RFP) are view-only and raise needs via the
  // Internal Support form.
  const canManageTickets = isAdmin || isSupport || isOrders || isQuotes

  const value: UserContextType = {
    currentUser,
    userType,
    effectiveUserType,
    setCurrentUser: handleSetCurrentUser,
    isLoading,
    isAdmin,
    isSuperAdmin,
    isSupport,
    isMarketing,
    isOrders,
    isQuotes,
    isRfp,
    canManageTickets,
    impersonatedUser,
    isImpersonating,
    startImpersonation,
    endImpersonation,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

// Custom hook to use the UserContext
export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

// Convenience hook to get just the user type
export function useUserRole() {
  const { userType } = useUser()
  return userType
}
